// BlogPost interface - matches the database structure
interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface LocalBusinessSchema {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  url: string;
  telephone: string;
  email: string;
  address: {
    '@type': string;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo: {
    '@type': string;
    latitude: number;
    longitude: number;
  };
  openingHours: string[];
  priceRange: string;
  serviceArea: {
    '@type': string;
    name: string;
  };
  hasOfferCatalog: {
    '@type': string;
    name: string;
    itemListElement: Array<{
      '@type': string;
      name: string;
      description: string;
    }>;
  };
  aggregateRating?: {
    '@type': string;
    ratingValue: string;
    reviewCount: string;
  };
}

export interface ArticleSchema {
  '@context': string;
  '@type': string;
  headline: string;
  description: string;
  image?: {
    '@type': string;
    url: string;
    creditText: string;
    license: string;
    acquireLicensePage: string;
    copyrightNotice: string;
  };
  author: {
    '@type': string;
    name: string;
  };
  publisher: {
    '@type': string;
    name: string;
    logo: {
      '@type': string;
      url: string;
      creditText: string;
      license: string;
      acquireLicensePage: string;
      copyrightNotice: string;
    };
  };
  datePublished: string;
  dateModified: string;
  mainEntityOfPage: {
    '@type': string;
    '@id': string;
  };
  keywords: string;
}

export interface ServiceSchema {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  provider: {
    '@type': string;
    name: string;
    url: string;
    telephone: string;
    address: {
      '@type': string;
      addressLocality: string;
      addressRegion: string;
      addressCountry: string;
    };
  };
  areaServed: {
    '@type': string;
    name: string;
  };
  serviceType: string;
  url: string;
}

export interface BreadcrumbSchema {
  '@context': string;
  '@type': string;
  itemListElement: Array<{
    '@type': string;
    position: number;
    name: string;
    item: string;
  }>;
}

export interface FAQSchema {
  '@context': string;
  '@type': string;
  mainEntity: Array<{
    '@type': string;
    name: string;
    acceptedAnswer: {
      '@type': string;
      text: string;
    };
  }>;
}

export const generateLocalBusinessSchema = (
  locationName: string,
  latitude?: number,
  longitude?: number,
  zipCodes?: string[]
): LocalBusinessSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'RoofingContractor',
    name: `Carolina Trophy Roofs - ${locationName}`,
    description: `Professional roofing contractor serving ${locationName}, North Carolina. Expert roof repair, replacement, and installation services with A+ BBB rating.`,
    url: `https://carolinatrophyroofs.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}`,
    telephone: '(252) 302-2003',
    email: 'info@carolinatrophyroofs.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Roofing Way', // Replace with actual address
      addressLocality: locationName,
      addressRegion: 'NC',
      postalCode: zipCodes?.[0] || '',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: latitude || 35.9382,
      longitude: longitude || -77.7905
    },
    openingHours: [
      'Mo-Fr 08:00-18:00',
      'Sa 09:00-16:00'
    ],
    priceRange: '$$',
    serviceArea: {
      '@type': 'State',
      name: 'Eastern North Carolina'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Roofing Services',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Roof Repair',
          description: 'Professional roof repair services for all roofing materials'
        },
        {
          '@type': 'Offer',
          name: 'Roof Replacement',
          description: 'Complete roof replacement with premium materials and warranty'
        },
        {
          '@type': 'Offer',
          name: 'Roof Installation',
          description: 'New roof installation for residential and commercial properties'
        },
        {
          '@type': 'Offer',
          name: 'Storm Damage Repair',
          description: 'Emergency storm damage assessment and repair services'
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '150'
    }
  };
};

export const generateArticleSchema = (
  post: BlogPost,
  currentUrl: string
): ArticleSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.seo_title || post.title,
    description: post.seo_description || post.excerpt || '',
    image: post.featured_image ? {
      '@type': 'ImageObject',
      url: post.featured_image,
      creditText: 'Carolina Trophy Roofs',
      license: 'https://carolinatrophyroofs.com/license',
      acquireLicensePage: 'https://carolinatrophyroofs.com/contact',
      copyrightNotice: '© Carolina Trophy Roofs. All rights reserved.'
    } : undefined,
    author: {
      '@type': 'Person',
      name: post.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'Carolina Trophy Roofs',
      logo: {
        '@type': 'ImageObject',
        url: 'https://carolinatrophyroofs.com/logo.png',
        creditText: 'Carolina Trophy Roofs',
        license: 'https://carolinatrophyroofs.com/license',
        acquireLicensePage: 'https://carolinatrophyroofs.com/contact',
        copyrightNotice: '© Carolina Trophy Roofs. All rights reserved.'
      }
    },
    datePublished: post.published_date || post.created_at || '',
    dateModified: post.updated_at || post.created_at || '',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl
    },
    keywords: post.seo_keywords?.join(', ') || post.tags?.join(', ') || ''
  };
};

export const generateServiceSchema = (
  serviceName: string,
  description: string,
  locationName: string
): ServiceSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description: description,
    provider: {
      '@type': 'RoofingContractor',
      name: 'Carolina Trophy Roofs',
      url: 'https://carolinatrophyroofs.com',
      telephone: '(252) 302-2003',
      address: {
        '@type': 'PostalAddress',
        addressLocality: locationName,
        addressRegion: 'NC',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'State',
      name: 'Eastern North Carolina'
    },
    serviceType: 'Roofing Service',
    url: `https://carolinatrophyroofs.com/services/${serviceName.toLowerCase().replace(/\s+/g, '-')}`
  };
};

export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>): BreadcrumbSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>): FAQSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const injectSchema = (schema: any) => {
  if (typeof window === 'undefined') return;
  
  // Remove existing schema script if it exists
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create and inject new schema script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};